import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi } from '../services/api';
import { User } from '../types';

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  isAdmin: boolean;
  isManager: boolean;
  isUser: boolean;
  isGuest: boolean;
  hasRole: (roleName: string) => boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      setIsAuthenticated(true);
      authApi.getMe().then((u) => setUser(u)).catch(() => {
        localStorage.removeItem('access_token');
        setIsAuthenticated(false);
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (username: string, password: string) => {
    const response = await authApi.login(username, password);
    localStorage.setItem('access_token', response.access_token);
    localStorage.setItem('refresh_token', response.refresh_token);
    setIsAuthenticated(true);
    try {
      const u = await authApi.getMe();
      setUser(u);
    } catch {}
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    setIsAuthenticated(false);
  };

  const isAdmin = user?.roles?.some((r) => r.name === 'admin') || false;
  const isManager = user?.roles?.some((r) => r.name === 'manager') || false;
  const isUser = user?.roles?.some((r) => r.name === 'users') || false;
  const isGuest = user?.roles?.some((r) => r.name === 'guest') || false;

  const hasRole = (roleName: string) => user?.roles?.some((r) => r.name === roleName) || false;

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, isAdmin, isManager, isUser, isGuest, hasRole, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
